import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { addressSlotKey, type AddressSnapshotFields, concatAddress } from "../addressVerification";
import type { AddressVerificationModalState } from "../useAddressVerificationGate";
import { ADDRESS_VERIFICATION_MESSAGE_KEYS, AddressVerificationPanel } from "./AddressVerificationPanel";

// ---------------------------------------------------------------------------
// AddressVerificationModal (address-standardization-spec.md §8)
//
// Single modal collapsing legacy's two nested popups (suggested-vs-actual
// picker + ignore confirmation). The body lives in AddressVerificationPanel,
// shared with the residence Call Script's in-modal address-compare step.
//
// To fix an address, the analyst uses "Edit address" (spec §8.1's
// ErrorSubpremise/ErrorFailed path), which closes this modal and lets them
// fix the fields on the main form.
// ---------------------------------------------------------------------------

interface AddressVerificationModalProperties {
  state?: AddressVerificationModalState;
  onSelect: (fields: AddressSnapshotFields) => void;
  onIgnore: () => void;
  /** Called with the "You Entered" address, so the analyst can fix it on the main form. */
  onEdit: (fields: AddressSnapshotFields) => void;
  onCancel: () => void;
}

export function AddressVerificationModal({
  state,
  onSelect,
  onIgnore,
  onEdit,
  onCancel,
}: AddressVerificationModalProperties) {
  const { t } = useTranslation();

  if (!state) {
    return (
      <Dialog open={false} onOpenChange={() => {}}>
        <DialogContent />
      </Dialog>
    );
  }

  const personLabel = t(state.slot.personPath === "driver" ? "signers.driver" : "signers.secondSigner");
  const slotLabel = t(
    state.slot.kind === "physical"
      ? "review.customer.residence.physicalAddress"
      : "review.customer.residence.mailingAddress"
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent showCloseButton={false} className="gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="bg-primary text-primary-foreground flex items-center justify-between px-5 py-2.5">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">
            {t("review.customer.residence.addressVerification.title", { personLabel, slotLabel })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t(ADDRESS_VERIFICATION_MESSAGE_KEYS[state.status])}
          </DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground inline-flex items-center transition-colors"
            aria-label={t("review.customer.residence.addressVerification.close")}
          >
            <X className="size-3.5" aria-hidden="true" />
          </DialogClose>
        </div>

        {/* Remount per slot/status/address so the ignore confirmation resets. */}
        <AddressVerificationPanel
          key={`${addressSlotKey(state.slot)}-${state.status}-${concatAddress(state.entered)}`}
          status={state.status}
          entered={state.entered}
          suggested={state.suggested}
          onSelect={onSelect}
          onIgnore={onIgnore}
          onEdit={onEdit}
          onCancel={onCancel}
        />
      </DialogContent>
    </Dialog>
  );
}
